import {
  TYPE,
  useProblemCarRpt,
  useProblemCarRptExport,
} from '@services/report'
import Flex from '@shared/Flex'
import { Button } from '@shared/Form'
import Layout from '@shared/Layout'
import Table from '@shared/Table'
import useSearch from '@shared/innmaxLib/hooks/useSearch'
import { rules } from '@shared/innmaxLib/utils/form'
import { downloadFile } from '@shared/utils/webHelper'
import { Form } from 'antd'
import { ColumnsType } from 'antd/es/table'
import moment from 'moment'
import { isEmpty } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FormItem } from 'src/components/Form'
import OptionsSelect from 'src/components/OptionsSelect'
import ParkingImageViewer from 'src/components/ParkingImageViewer'

const initialReportType = {
  reportType: TYPE.YEAR,
  problemType: 0,
  conditionDate: new Date().getFullYear(),
  startAt: moment().startOf('year').valueOf(),
  endAt: moment().endOf('year').valueOf(),
  pageSize: 10,
  pageNum: 0,
}

function ProblemCar() {
  const { t } = useTranslation()

  const [form] = Form.useForm()

  const [currentType, setCurrentType] = React.useState(TYPE.YEAR)

  const { query, handleSearch, handleTableChange, isTriggerSearch } = useSearch(
    {
      form,
      keepPreviousQuery: false,
      dateRangeField: ['startAt', 'endAt'],
      initial: initialReportType,
    }
  )

  const handleReportType = (val: any) => {
    setCurrentType(val?.reportType)
    return val
  }

  const { loading, dataSource } = useProblemCarRpt(query, {
    enabled: isTriggerSearch && !isEmpty(query),
  })

  const [exporting, handleExport] = useProblemCarRptExport(query, {
    onSuccess: (blob: any) => {
      downloadFile({
        blob,
        filename: 'report',
      })
    },
  })

  const columns: ColumnsType<any> = [
    {
      title: '通報日期',
      dataIndex: 'reportDateTimeStr',
      align: 'center',
    },
    {
      title: '縣市',
      dataIndex: 'cityName',
      align: 'center',
    },
    {
      title: '航廈',
      dataIndex: 'districtName',
      align: 'center',
    },
    {
      title: '站點',
      dataIndex: 'parkingLotName',
      align: 'center',
    },
    {
      title: '查報項目',
      dataIndex: 'problemTypeStr',
      align: 'center',
    },
    {
      title: '格位編號',
      dataIndex: 'parkingLotNumber',
      align: 'center',
    },
    {
      title: '問題車牌',
      dataIndex: 'lprNo',
      align: 'center',
    },
    {
      title: '開始停車時間',
      dataIndex: 'parkingStartTimeStr',
      align: 'center',
    },
    {
      title: '結束停車時間',
      dataIndex: 'parkingEndTimeStr',
      align: 'center',
    },
    {
      title: '查看照片',
      key: 'view',
      align: 'center',
      dataIndex: 'mainOrderNo',
      width: 80,
      render: (mainOrderNo, r) => (
        <ParkingImageViewer
          width={800}
          title={
            <div className="text-lg text-white">{`${r.cityName} ${r.districtName} ${r.parkingLotName} / ${r.problemTypeStr}`}</div>
          }
          description={
            <div className="text-sm text-yellow-100">
              {`停車起迄: ${r.parkingStartTimeStr} - ${r.parkingEndTimeStr} / 車格: ${r.parkingLotNumber} / 車牌: ${r.lprNo}`}
            </div>
          }
          mainOrderNo={mainOrderNo}
        />
      ),
    },
  ]

  return (
    <>
      <Layout>
        <Form layout="inline" form={form} initialValues={initialReportType}>
          <FormItem label={'T2/T3'} name="sessionId">
            <OptionsSelect.SessionSelect className="w-130" />
          </FormItem>

          <FormItem
            label={'查報項目'}
            name="problemType"
            rules={[rules.required]}>
            <OptionsSelect.ProblemCarItemSelect isShowAll />
          </FormItem>

          <FormItem
            label={`${t('report:report type')} / ${t('report:time')}`}
            name="reportTime"
            initialValue={initialReportType}
            className={currentType === TYPE.DAY ? 'w-[380px]' : 'w-[250px]'}
            getValueFromEvent={handleReportType}>
            <OptionsSelect.ReportRangeTimeSelect />
          </FormItem>

          <FormItem.Button>
            <Button
              loading={false}
              type="primary"
              className="w-96"
              onClick={handleSearch}>
              {t('common:search')}
            </Button>
          </FormItem.Button>
          <FormItem.Button>
            <Button
              loading={exporting}
              type="primary"
              ghost
              className="w-96"
              onClick={handleExport}>
              {t('report:export')}
            </Button>
          </FormItem.Button>
        </Form>

        <div className="mt-44 mb-16">
          <Flex between>
            <Table.RowCount count={dataSource.total} />
          </Flex>
        </div>
        <Table
          columns={columns}
          loading={loading}
          dataSource={dataSource}
          onChange={handleTableChange}
        />
      </Layout>
    </>
  )
}

export default React.memo(ProblemCar)
